<script setup>
import { Icon } from '@dashhudson/dashing-ui/icon';
import { LottieAnimation } from '@dashhudson/dashing-ui/lottie-animation';
import { computed, ref } from 'vue';

import visionAiSphereBlue from '@/assets/images/animations/vision-ai-sphere-blue.json';
import LegacyIcon from '@/components/foundation/Icon.vue';
import { useNewChatDisabled } from '@/components/VisionAi/composables/useNewChatDisabled';
import { NEW_CHAT_TOOLTIP, VISION_AI_WIDGET_INTERACTIONS } from '@/components/VisionAi/constants';
import { aiWidgetInteractionEvent } from '@/components/VisionAi/utils/mixpanel-events';
import VisionToolbar from '@/components/VisionAi/VisionToolbar.vue';
import { env } from '@/env';
import { useVisionAiStore } from '@/stores/vision-ai';
import { useVisionAiConversationsStore } from '@/stores/vision-ai-conversations';
import { useVisionAiMessagesStore } from '@/stores/vision-ai-messages';
import { colours } from '@/ux/colours';

const props = defineProps({
  chatPopupIsExpanded: { type: Boolean, default: false },
  showingConversationHistory: { type: Boolean, default: false },
});

const visionAiConversationsStore = useVisionAiConversationsStore();
const visionAiStore = useVisionAiStore();
const visionAiMessagesStore = useVisionAiMessagesStore();

const hoverOnButton = ref(false);

const emit = defineEmits([
  'closeChatWindow',
  'clearChatHistory',
  'launchVisionAiResourceCenter',
  'expandChatPopup',
  'shrinkChatPopup',
  'showConversationHistory',
  'hideConversationHistory',
]);

const hasConversations = computed(
  () =>
    visionAiConversationsStore.conversations.length > 0 || visionAiMessagesStore.anySummaryLoaded,
);

const sphereAnimationProps = computed(() => ({
  animationLink: visionAiSphereBlue,
  width: '1.75rem',
  height: '1.75rem',
  renderer: 'canvas',
}));

const showLegacyIcon = computed(() => env.isProduction);

const resizeIcon = computed(() => {
  if (props.chatPopupIsExpanded) {
    return {
      dataTest: 'shrink-chat-popup',
      buttonClass: 'shrink-icon',
      clickEvent: (event) => {
        emit('shrinkChatPopup', event);
        aiWidgetInteractionEvent({
          interaction: VISION_AI_WIDGET_INTERACTIONS.SHRINK,
        });
      },
      iconName: 'shrinkDiagonal',
      tooltip: {
        content: 'Minimize Window',
        boundary: 'viewport',
        placement: 'top-end',
      },
      iconHoverColor: colours.TEXT.TEXT_PRIMARY,
      showBorderOnHover: true,
      iconHeight: '32px',
      iconWidth: '32px',
    };
  }
  return {
    dataTest: 'expand-chat-popup',
    buttonClass: 'expand-icon',
    clickEvent: (event) => {
      emit('expandChatPopup', event);
      aiWidgetInteractionEvent({
        interaction: VISION_AI_WIDGET_INTERACTIONS.EXPAND,
      });
    },
    iconName: 'expandDiagonal',
    tooltip: {
      content: 'Expand Window',
      boundary: 'viewport',
      placement: 'top-end',
    },
    iconHoverColor: colours.TEXT.TEXT_PRIMARY,
    showBorderOnHover: true,
    iconHeight: '32px',
    iconWidth: '32px',
  };
});

const conversationHistoryIcon = computed(() => {
  if (!props.showingConversationHistory) {
    return {
      dataTest: 'show-chat-history',
      buttonClass: 'shrink-icon',
      clickEvent: (event) => {
        emit('showConversationHistory', event);
        aiWidgetInteractionEvent({
          interaction: VISION_AI_WIDGET_INTERACTIONS.SHOW_CHAT_HISTORY,
        });
      },
      iconName: 'chatHistory',
      tooltip: {
        content: 'Show Chat History',
        boundary: 'viewport',
        placement: 'top-end',
      },
      iconHoverColor: colours.TEXT.TEXT_PRIMARY,
      showBorderOnHover: true,
      iconHeight: '32px',
      iconWidth: '32px',
    };
  }

  return {
    dataTest: 'hide-chat-history',
    buttonClass: 'shrink-icon',
    clickEvent: (event) => {
      emit('hideConversationHistory', event);
      aiWidgetInteractionEvent({
        interaction: VISION_AI_WIDGET_INTERACTIONS.HIDE_CHAT_HISTORY,
      });
    },
    iconName: 'hideChatHistory',
    tooltip: {
      content: 'Hide Chat History',
      boundary: 'viewport',
      placement: 'top-end',
    },
    iconHoverColor: colours.TEXT.TEXT_PRIMARY,
    showBorderOnHover: true,
    iconHeight: '32px',
    iconWidth: '32px',
  };
});

const { newChatDisabled } = useNewChatDisabled();

const newChatTooltip = computed(() => {
  let tooltip;
  if (visionAiStore.canAccessVisionAiButNotConversational) {
    tooltip = NEW_CHAT_TOOLTIP.UPGRADE;
  } else if (newChatDisabled.value) {
    tooltip = NEW_CHAT_TOOLTIP.DISABLED;
  } else {
    tooltip = NEW_CHAT_TOOLTIP.ENABLED;
  }
  return {
    content: tooltip,
    boundary: 'viewport',
    placement: 'top-end',
  };
});

const showNewChat = computed(
  () => !props.chatPopupIsExpanded && visionAiStore.canAccessVisionAiTextbox,
);

const toolbarIconsProps = computed(() =>
  // Properties needed to properly render each Button component
  [
    resizeIcon.value,
    conversationHistoryIcon.value,
    {
      buttonClass: 'help-icon',
      clickEvent: (event) => {
        emit('launchVisionAiResourceCenter', event);
        aiWidgetInteractionEvent({
          interaction: VISION_AI_WIDGET_INTERACTIONS.HELP,
        });
      },
      iconName: 'question-mark',
      tooltip: {
        content: 'Help',
        boundary: 'viewport',
        placement: 'top-end',
      },
      iconHoverColor: colours.TEXT.TEXT_PRIMARY,
      showBorderOnHover: true,
      iconHeight: '24px',
      iconWidth: '24px',
    },
    {
      dataTest: 'clear-chat-history',
      buttonClass: 'clear-chat-history',
      disabled: !hasConversations.value,
      clickEvent: (event) => {
        emit('clearChatHistory', event);
        aiWidgetInteractionEvent({
          interaction: VISION_AI_WIDGET_INTERACTIONS.DELETE_ICON_CLICKED,
        });
      },
      hoverColor: !hasConversations.value ? null : colours.ERROR.ERROR_500,
      iconName: 'bin',
      iconHoverColor: !hasConversations.value ? null : colours.ERROR.ERROR_500,
      showBorderOnHover: hasConversations.value,
      iconHeight: '24px',
      iconWidth: '24px',
      tooltip: {
        content: 'Delete Entire Chat History',
        boundary: 'viewport',
        placement: 'top-end',
      },
      mouseEnterEvent: () => {
        hoverOnButton.value = true;
      },
      mouseLeaveEvent: () => {
        hoverOnButton.value = false;
      },
    },
    {
      dataTest: 'close-chat-popup',
      buttonClass: 'border-action-200 mt-4',
      clickEvent: (event) => emit('closeChatWindow', event),
      iconName: 'chevronDown',
      iconHoverColor: colours.TEXT.TEXT_PRIMARY,
      iconHeight: '24px',
      iconWidth: '24px',
    },
  ],
);

async function startNewChat() {
  if (newChatDisabled.value) return;

  aiWidgetInteractionEvent({
    interaction: VISION_AI_WIDGET_INTERACTIONS.NEW_CHAT,
  });
  visionAiStore.clearLocalChatHistory();
  await visionAiConversationsStore.createConversation();
}

defineExpose(
  process.env.NODE_ENV === 'test'
    ? {
        resizeIcon,
        conversationHistoryIcon,
        toolbarIconsProps,
      }
    : {},
);
</script>

<template>
  <VisionToolbar display-vertically :clickable-icons="toolbarIconsProps" class="!w-8 !pt-6">
    <template #leftIcons>
      <div class="flex flex-col items-center justify-end gap-4">
        <LottieAnimation v-if="showLegacyIcon" v-bind="sphereAnimationProps" />
        <Icon
          v-else
          name="vision-ai"
          data-test="vision-ai-icon"
          size="24"
          class="fill-icon-vision-ai"
        />
        <transition name="fade">
          <LegacyIcon
            v-if="showNewChat"
            v-tooltip="newChatTooltip"
            data-test="sidebarNewChat"
            name="newChat"
            :color="colours.TEXT.TEXT_LINK"
            medium
            :class="newChatDisabled ? '!cursor-not-allowed !opacity-50' : 'cursor-pointer'"
            @click="startNewChat"
          />
        </transition>
      </div>
    </template>

    <template #clickableIcons="{ iconName, iconColor, iconHeight, iconWidth, iconHoverColor }">
      <LegacyIcon
        :name="iconName"
        :color="hoverOnButton && hasConversations.value ? iconHoverColor : iconColor"
        :hover-color="iconHoverColor"
        :height="iconHeight"
        :width="iconWidth"
      />
    </template>
  </VisionToolbar>
</template>
